import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiMapPin, FiUser, FiHeart, FiCheck, FiX } from 'react-icons/fi';
import { GiLovers, GiHearts, GiRing } from 'react-icons/gi';
import axios from 'axios';
import SEO from '../components/common/SEO';
import { useHydrated } from '../hooks/useHydrated';
import DateTimePicker from '../components/common/DateTimePicker';
import { matchMakingAPI } from '../services/api';
import './MatchMaking.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/** City search hook - reusable for bride and groom */
function useCitySearch() {
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchCities = useCallback(async (query) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const response = await axios.get(`${API_BASE_URL}/panchang/cities`, {
        params: { q: query }
      });
      setSuggestions(response.data.data || []);
      setShowSuggestions(true);
    } catch {
      setSuggestions([]);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (input.length >= 2 && !selectedLocation) {
        searchCities(input);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [input, searchCities, selectedLocation]);

  const handleInputChange = (value) => {
    setInput(value);
    setSelectedLocation(null);
  };

  const handleSelect = (city) => {
    const displayName = city.state
      ? `${city.city}, ${city.state}, ${city.country}`
      : `${city.city}, ${city.country}`;
    setInput(displayName);
    setSelectedLocation(city);
    setShowSuggestions(false);
  };

  return {
    input, suggestions, showSuggestions, selectedLocation, ref,
    handleInputChange, handleSelect,
    setInput, setSelectedLocation, // exposed for programmatic pre-fill
  };
}

const MatchMaking = () => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const [brideData, setBrideData] = useState({ name: '', date: '', time: '' });
  const [groomData, setGroomData] = useState({ name: '', date: '', time: '' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const resultRef = useRef(null);

  useEffect(() => {
    if (result && resultRef.current) {
      setTimeout(() => {
        resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [result]);

  const brideCity = useCitySearch();
  const groomCity = useCitySearch();
  const hydrated = useHydrated();

  // Pre-fill + auto-submit when arriving from a PSEO /guide page that passes
  // { brideData, groomData, brideLocation, groomLocation } via router state.
  useEffect(() => {
    const ls = routerLocation.state;
    if (!ls || !ls.brideData || !ls.groomData) return;

    setBrideData({
      name: ls.brideData.name || '',
      date: ls.brideData.date || '',
      time: ls.brideData.time || '',
    });
    setGroomData({
      name: ls.groomData.name || '',
      date: ls.groomData.date || '',
      time: ls.groomData.time || '',
    });
    if (ls.brideData.place) brideCity.setInput(ls.brideData.place);
    if (ls.groomData.place) groomCity.setInput(ls.groomData.place);
    if (ls.brideLocation) brideCity.setSelectedLocation(ls.brideLocation);
    if (ls.groomLocation) groomCity.setSelectedLocation(ls.groomLocation);

    // Clear router state so it doesn't re-trigger on subsequent re-renders
    window.history.replaceState({}, document.title);

    // Auto-submit only when we have everything needed
    const ready =
      ls.brideData.name && ls.brideData.date && ls.brideData.time && ls.brideData.place &&
      ls.groomData.name && ls.groomData.date && ls.groomData.time && ls.groomData.place;
    if (!ready) return;

    (async () => {
      setLoading(true);
      setError('');
      try {
        const buildPerson = async (d, cityLoc) => {
          const person = { name: d.name, date: d.date, time: d.time, place: d.place };
          let city = cityLoc;
          if (!city && d.place) {
            const res = await axios.get(`${API_BASE_URL}/panchang/cities`, { params: { q: d.place } });
            city = (res.data.data || [])[0];
          }
          if (city) {
            person.lat = city.latitude;
            person.lon = city.longitude;
            person.tz = city.timezone;
            person.place = city.city;
            person.state = city.state;
            person.country = city.country;
          }
          return person;
        };

        const bride = await buildPerson(ls.brideData, ls.brideLocation);
        const groom = await buildPerson(ls.groomData, ls.groomLocation);
        const response = await matchMakingAPI.checkCompatibility({ bride, groom });
        if (response.data.success) setResult(response.data.data);
        else setError(response.data.error || 'Failed to check compatibility');
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to check compatibility');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerLocation.state]);

  const handleBrideChange = (e) => {
    setBrideData({ ...brideData, [e.target.name]: e.target.value });
  };

  const handleGroomChange = (e) => {
    setGroomData({ ...groomData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const bride = {
        name: brideData.name,
        date: brideData.date,
        time: brideData.time,
        place: brideCity.input,
      };
      const groom = {
        name: groomData.name,
        date: groomData.date,
        time: groomData.time,
        place: groomCity.input,
      };

      if (brideCity.selectedLocation) {
        bride.lat = brideCity.selectedLocation.latitude;
        bride.lon = brideCity.selectedLocation.longitude;
        bride.tz = brideCity.selectedLocation.timezone;
        bride.place = brideCity.selectedLocation.city;
        bride.state = brideCity.selectedLocation.state;
        bride.country = brideCity.selectedLocation.country;
      }

      if (groomCity.selectedLocation) {
        groom.lat = groomCity.selectedLocation.latitude;
        groom.lon = groomCity.selectedLocation.longitude;
        groom.tz = groomCity.selectedLocation.timezone;
        groom.place = groomCity.selectedLocation.city;
        groom.state = groomCity.selectedLocation.state;
        groom.country = groomCity.selectedLocation.country;
      }

      const response = await matchMakingAPI.checkCompatibility({ bride, groom });
      if (response.data.success) {
        setResult(response.data.data);
      } else {
        setError(response.data.error || 'Failed to check compatibility');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to check compatibility');
    } finally {
      setLoading(false);
    }
  };

  const getStatusClass = (val) => {
    if (val === 3) return 'good';
    if (val === 2) return 'average';
    return 'poor';
  };

  const getStatusIcon = (val) => {
    if (val === 3) return <FiCheck />;
    if (val === 2) return '~';
    return <FiX />;
  };

  return (
    <div className="matchmaking-page">
      <SEO
        title="Marriage Matching - 12 Porutham Compatibility Check"
        description="Free Tamil 12 Porutham horoscope matching for marriage. Check bride and groom compatibility using Vedic astrology. Get Rajju, Nadi, and Manglik analysis instantly."
        keywords="marriage matching, kundli matching, horoscope matching, 12 porutham, marriage compatibility, vedic matching, gun milan"
        canonicalPath="/match-making"
        hreflang={[
          { lang: 'en', path: '/match-making' },
          { lang: 'ta', path: '/jathagam-porutham-in-tamil-for-marriage' },
        ]}
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "Marriage Compatibility Checker",
          "applicationCategory": "LifestyleApplication",
          "description": "Check marriage compatibility using the Tamil 12 Porutham system",
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" }
        }, {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {"@type": "Question", "name": "What is a good Porutham score for marriage?", "acceptedAnswer": {"@type": "Answer", "text": "A score of 10 or more out of 12 is considered excellent. 8-9 is good and generally acceptable for marriage. 6-7 is average and requires consultation with an astrologer to review individual factors. Below 6 is generally not recommended. However, the presence of Rajju Dosha or Nadi Dosha (regardless of score) is considered serious and requires expert consultation."}},
            {"@type": "Question", "name": "Can a marriage proceed with Rajju Dosha?", "acceptedAnswer": {"@type": "Answer", "text": "Rajju Dosha is traditionally considered very serious. However, certain planetary positions in the individual birth charts can neutralize Rajju Dosha \u2014 for example, if Jupiter aspects the 7th house or Venus is very strong. In some traditions, specific rituals and remedies are prescribed. An experienced astrologer should evaluate the full birth charts before a final decision."}},
            {"@type": "Question", "name": "Is Manglik Dosha the same as Kuja Dosha?", "acceptedAnswer": {"@type": "Answer", "text": "Yes, Manglik Dosha and Kuja Dosha both refer to the same condition \u2014 Mars (Mangal/Kuja) placed in the 1st, 2nd, 4th, 7th, 8th, or 12th house of the birth chart. It is believed to create obstacles in marriage. The traditional remedy is that a Manglik person marrying another Manglik cancels the dosha. Many modern astrologers also consider the strength of Mars and benefic aspects before declaring a full Manglik Dosha."}},
            {"@type": "Question", "name": "What information is needed for horoscope matching?", "acceptedAnswer": {"@type": "Answer", "text": "You need the exact date of birth, time of birth, and place of birth for both the bride and groom. The birth star (Nakshatra) and Moon sign (Rashi) are automatically calculated from this data. For maximum accuracy, ensure the birth time is correct \u2014 even a 30-minute difference can sometimes change the Nakshatra pada, affecting certain Porutham calculations."}}
          ]
        }]}
      />
      <div className="container">
        <motion.div
          className="page-header"
          initial={hydrated ? { opacity: 0, y: -20 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="header-icon">
            <GiLovers />
          </div>
          <h1>Horoscope <span className="gradient-text">Matching</span></h1>
          <p>Check marriage compatibility using the Tamil 12 Porutham system - the ancient Vedic matching method</p>
        </motion.div>

        <div className="matchmaking-content">
          <motion.form
            className="matching-form"
            onSubmit={handleSubmit}
            initial={hydrated ? { opacity: 0, y: 20 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="form-columns">
              {/* Bride Details */}
              <div className="form-column bride-column">
                <div className="column-header">
                  <span className="column-icon bride-icon">👰</span>
                  <h3>Bride Details</h3>
                </div>
                <div className="form-card">
                  <div className="form-group">
                    <label><FiUser className="input-icon" /> Full Name</label>
                    <input type="text" name="name" value={brideData.name} onChange={handleBrideChange} placeholder="Bride's full name" required />
                  </div>
                  <DateTimePicker
                    date={brideData.date}
                    time={brideData.time}
                    onDateChange={(val) => setBrideData(prev => ({ ...prev, date: val }))}
                    onTimeChange={(val) => setBrideData(prev => ({ ...prev, time: val }))}
                  />
                  <div className="form-group city-search-group" ref={brideCity.ref}>
                    <label><FiMapPin className="input-icon" /> Place of Birth</label>
                    <input
                      type="text"
                      value={brideCity.input}
                      onChange={(e) => brideCity.handleInputChange(e.target.value)}
                      placeholder="Search city..."
                      required
                      autoComplete="off"
                    />
                    {brideCity.showSuggestions && brideCity.suggestions.length > 0 && (
                      <ul className="city-suggestions">
                        {brideCity.suggestions.map((city, i) => (
                          <li key={i} onClick={() => brideCity.handleSelect(city)}>
                            {city.city}{city.state ? `, ${city.state}` : ''}, {city.country}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>

              {/* Heart Connector */}
              <div className="heart-connector">
                <GiHearts className="heart-icon" />
              </div>

              {/* Groom Details */}
              <div className="form-column groom-column">
                <div className="column-header">
                  <span className="column-icon groom-icon">🤵</span>
                  <h3>Groom Details</h3>
                </div>
                <div className="form-card">
                  <div className="form-group">
                    <label><FiUser className="input-icon" /> Full Name</label>
                    <input type="text" name="name" value={groomData.name} onChange={handleGroomChange} placeholder="Groom's full name" required />
                  </div>
                  <DateTimePicker
                    date={groomData.date}
                    time={groomData.time}
                    onDateChange={(val) => setGroomData(prev => ({ ...prev, date: val }))}
                    onTimeChange={(val) => setGroomData(prev => ({ ...prev, time: val }))}
                  />
                  <div className="form-group city-search-group" ref={groomCity.ref}>
                    <label><FiMapPin className="input-icon" /> Place of Birth</label>
                    <input
                      type="text"
                      value={groomCity.input}
                      onChange={(e) => groomCity.handleInputChange(e.target.value)}
                      placeholder="Search city..."
                      required
                      autoComplete="off"
                    />
                    {groomCity.showSuggestions && groomCity.suggestions.length > 0 && (
                      <ul className="city-suggestions">
                        {groomCity.suggestions.map((city, i) => (
                          <li key={i} onClick={() => groomCity.handleSelect(city)}>
                            {city.city}{city.state ? `, ${city.state}` : ''}, {city.country}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="form-group form-lang-selector" style={{ maxWidth: '200px' }}>
              <label>Language</label>
              <select
                value="en"
                onChange={(e) => {
                  if (e.target.value === 'ta') {
                    navigate('/jathagam-porutham-in-tamil-for-marriage');
                  }
                }}
              >
                <option value="en">English</option>
                <option value="ta">தமிழ் (Tamil)</option>
              </select>
            </div>

            <button type="submit" className="btn btn-primary btn-match" disabled={loading}>
              <FiHeart /> {loading ? 'Checking...' : 'Check Compatibility'}
            </button>

            {error && <div className="form-error-match">{error}</div>}
          </motion.form>

          {result && (
            <motion.div
              ref={resultRef}
              className="matching-results"
              initial={hydrated ? { opacity: 0, y: 30 } : false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Birth Star Info */}
              <div className="star-info-card">
                <div className="star-info-row">
                  <div className="star-person bride-star">
                    <span className="star-label">Bride</span>
                    <span className="star-name">{result.bride.name}</span>
                    <span className="star-detail">{result.bride.nakshatra} - {result.bride.rashi}</span>
                    <span className="star-detail">Pada {result.bride.pada}</span>
                  </div>
                  <div className="star-vs">
                    <GiHearts />
                  </div>
                  <div className="star-person groom-star">
                    <span className="star-label">Groom</span>
                    <span className="star-name">{result.groom.name}</span>
                    <span className="star-detail">{result.groom.nakshatra} - {result.groom.rashi}</span>
                    <span className="star-detail">Pada {result.groom.pada}</span>
                  </div>
                </div>
              </div>

              {/* Score Card */}
              <div className="score-card">
                <div className="score-header">
                  <GiRing className="score-icon" />
                  <h3>Compatibility Score</h3>
                </div>
                <div className="score-circle">
                  <svg viewBox="0 0 100 100">
                    <circle className="score-bg" cx="50" cy="50" r="45" />
                    <circle
                      className="score-progress"
                      cx="50" cy="50" r="45"
                      style={{ strokeDasharray: `${result.percentage * 2.83} 283` }}
                    />
                  </svg>
                  <div className="score-text">
                    <span className="score-number">{result.totalScore}</span>
                    <span className="score-max">/ {result.maxScore}</span>
                  </div>
                </div>
                <div className="score-percentage">{result.percentage}% Match</div>
                <div className={`recommendation ${result.percentage >= 70 ? 'good' : result.percentage >= 50 ? 'average' : 'poor'}`}>
                  {result.recommendation}
                </div>
              </div>

              {/* 12 Porutham Details */}
              <div className="guna-details">
                <h3>12 Porutham Analysis</h3>
                <p className="guna-subtitle">Detailed analysis of 12 Tamil marriage compatibility factors</p>

                <div className="guna-table">
                  <div className="guna-header">
                    <span>#</span>
                    <span>Porutham</span>
                    <span>Description</span>
                    <span>Result</span>
                  </div>
                  {result.poruthams && result.poruthams.map((p, index) => (
                    <motion.div
                      key={p.name}
                      className="guna-row"
                      initial={hydrated ? { opacity: 0, x: -20 } : false}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                    >
                      <span className="guna-num">{index + 1}</span>
                      <span className="guna-name">{p.name}</span>
                      <span className="guna-desc">{p.description}</span>
                      <span className={`guna-status ${getStatusClass(p.result)}`}>
                        {getStatusIcon(p.result)}
                      </span>
                    </motion.div>
                  ))}
                </div>

                <div className="porutham-summary">
                  <div className="summary-item good-count">
                    <FiCheck /> {result.goodCount} Good
                  </div>
                  <div className="summary-item moderate-count">
                    ~ {result.moderateCount} Moderate
                  </div>
                  <div className="summary-item bad-count">
                    <FiX /> {result.badCount} Not Matching
                  </div>
                </div>
              </div>

              {/* Manglik Status */}
              {result.manglik && (
                <div className="manglik-card">
                  <h3>Manglik Status</h3>
                  <div className="manglik-grid">
                    <div className="manglik-item">
                      <span className="manglik-label">Bride</span>
                      <span className={`manglik-status ${result.manglik.bride.isManglik ? 'manglik' : 'non-manglik'}`}>
                        {result.manglik.bride.isManglik ? `Manglik (${result.manglik.bride.severity})` : 'Non-Manglik'}
                      </span>
                    </div>
                    <div className="manglik-item">
                      <span className="manglik-label">Groom</span>
                      <span className={`manglik-status ${result.manglik.groom.isManglik ? 'manglik' : 'non-manglik'}`}>
                        {result.manglik.groom.isManglik ? `Manglik (${result.manglik.groom.severity})` : 'Non-Manglik'}
                      </span>
                    </div>
                  </div>
                  <div className={`manglik-compatibility ${result.manglik.compatible ? 'compatible' : 'incompatible'}`}>
                    {result.manglik.compatible ? (
                      <><FiCheck /> Manglik Dosha: Compatible</>
                    ) : (
                      <><FiX /> Manglik Dosha: Requires Remedies</>
                    )}
                  </div>
                </div>
              )}

              {/* Interpretation */}
              <div className="interpretation-card">
                <h3>Summary</h3>
                <p>
                  Based on the 12 Porutham analysis, this match scores <strong>{result.totalScore} out of {result.maxScore}</strong> points
                  ({result.percentage}%), which is considered a <strong>{result.percentage >= 75 ? 'highly favorable' : result.percentage >= 60 ? 'good' : result.percentage >= 50 ? 'moderate' : 'challenging'}</strong> match.
                  {result.goodCount >= 8 && ' The couple shows strong compatibility across most factors.'}
                  {result.badCount >= 4 && ' Several areas need attention for a harmonious relationship.'}
                  {' '}For a detailed personalized analysis and remedies, consult with an expert astrologer.
                </p>
              </div>
            </motion.div>
          )}
        </div>

        {/* Info Section */}
        <motion.div
          className="info-section"
          initial={hydrated ? { opacity: 0 } : false}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <h3>About 12 Porutham</h3>
          <div className="info-grid">
            <div className="info-item">
              <h4>What is Porutham?</h4>
              <p>Porutham is the Tamil system of horoscope matching that evaluates 12 different compatibility factors between bride and groom based on their birth stars (nakshatras) and moon signs.</p>
            </div>
            <div className="info-item">
              <h4>Key Poruthams</h4>
              <p>Rajju and Nadi are considered the most critical. Rajju checks marital bond strength while Nadi ensures health and genetic compatibility for offspring.</p>
            </div>
            <div className="info-item">
              <h4>Accurate Results</h4>
              <p>For accurate results, provide exact birth time and place. The Moon's nakshatra and rashi are calculated from your birth chart for precise matching.</p>
            </div>
          </div>
        </motion.div>

        {/* Bottom SEO Content */}
        <div className="page-bottom-content">
          <h2>Free Marriage Compatibility Check - Tamil 12 Porutham Matching</h2>
          <p>AstroEpanchang offers free horoscope matching using the traditional Tamil 12 Porutham system, the most widely followed marriage compatibility method in South Indian Vedic astrology. Our tool analyzes the bride and groom's birth stars (Nakshatras), Moon signs (Rashi), and planetary positions to evaluate 12 critical compatibility factors.</p>
          <p>The 12 Poruthams include Dina, Gana, Mahendra, Stree Deergha, Yoni, Rashi, Rasiyathipathi, Vasya, Rajju, Vedha, Nadi, and Ayul. Among these, Rajju Porutham (marital bond strength) and Nadi Porutham (health and offspring compatibility) are considered the most essential. A score of 8 or more out of 12 is generally considered a favorable match.</p>
          <p>Enter the birth details of both bride and groom to get instant compatibility analysis with detailed Porutham breakdown, Manglik Dosha check, and expert recommendations for a harmonious married life.</p>

          <h3>Understanding the 12 Poruthams in Detail</h3>
          <p><strong>Rajju Porutham (Most Essential):</strong> Rajju examines the longevity and safety of the husband. The 27 Nakshatras are grouped into five Rajju categories (Siro, Kanda, Udara, Kati, Pada). For a favorable match, the bride and groom must not belong to the same Rajju category. If they do, it is considered a major Rajju dosha affecting the husband's health and longevity.</p>
          <p><strong>Nadi Porutham (Most Essential):</strong> Nadi determines physical compatibility and the likelihood of healthy offspring. The 27 Nakshatras are divided into three Nadis — Aadi (Vata), Madhya (Pitta), and Antya (Kapha). Both partners must have different Nadi types. Nadi dosha (same Nadi) can cause health issues in children and marital conflicts. It is considered the most serious of all doshas.</p>
          <p><strong>Gana Porutham:</strong> Gana classifies Nakshatras into three groups — Deva (divine), Manushya (human), and Rakshasa (demonic) — based on temperament. Deva-Deva and Manushya-Manushya are ideal combinations. Deva-Manushya is acceptable. Rakshasa combinations with Deva are problematic. This porutham governs mental and temperamental compatibility.</p>
          <p><strong>Dina Porutham:</strong> Calculated by counting from the bride's birth star to the groom's and dividing by 9. The remainder (1,3,5,7) indicates auspiciousness. Dina Porutham relates to health, fortune, and overall compatibility in daily life.</p>
          <p><strong>Mahendra Porutham:</strong> Counted from the bride's star to the groom's star. If the count is 4, 7, 10, 13, 16, 19, 22, or 25, it is favorable. Mahendra Porutham ensures longevity, wealth, and progeny in the marriage.</p>
          <p><strong>Yoni Porutham:</strong> The 27 Nakshatras are classified into 14 animal symbols (Yonis). Compatible Yoni pairs include Horse-Horse, Elephant-Elephant, etc. Hostile Yoni combinations (like Cat-Rat, Dog-Deer) indicate friction in physical compatibility and intimate relationship.</p>

          <h3>North Indian Ashtakoota System vs South Indian 12 Porutham</h3>
          <p>The North Indian Ashtakoota (8 Kuta) system evaluates 8 compatibility factors with a total score of 36 points. A score above 18 is considered acceptable, and above 24 is excellent. The 8 Kutas are: Varna (1 point), Vashya (2), Tara (3), Yoni (4), Graha Maitri (5), Gana (6), Rashi (7), and Nadi (8). The South Indian 12 Porutham system offers a more detailed analysis with additional factors like Rajju, Mahendra, and Stree Deergha. Both systems identify Rajju and Nadi as the most critical factors that should not be violated regardless of the overall score.</p>

          <h3>Frequently Asked Questions About Marriage Compatibility</h3>
          <div className="faq-seo">
            <div className="faq-seo-item">
              <h4>What is a good Porutham score for marriage?</h4>
              <p>A score of 10 or more out of 12 is considered excellent. 8-9 is good and generally acceptable for marriage. 6-7 is average and requires consultation with an astrologer to review individual factors. Below 6 is generally not recommended. However, the presence of Rajju Dosha or Nadi Dosha (regardless of score) is considered serious and requires expert consultation.</p>
            </div>
            <div className="faq-seo-item">
              <h4>Can a marriage proceed with Rajju Dosha?</h4>
              <p>Rajju Dosha is traditionally considered very serious. However, certain planetary positions in the individual birth charts can neutralize Rajju Dosha — for example, if Jupiter aspects the 7th house or Venus is very strong. In some traditions, specific rituals and remedies are prescribed. An experienced astrologer should evaluate the full birth charts before a final decision.</p>
            </div>
            <div className="faq-seo-item">
              <h4>Is Manglik Dosha the same as Kuja Dosha?</h4>
              <p>Yes, Manglik Dosha and Kuja Dosha both refer to the same condition — Mars (Mangal/Kuja) placed in the 1st, 2nd, 4th, 7th, 8th, or 12th house of the birth chart. It is believed to create obstacles in marriage. The traditional remedy is that a Manglik person marrying another Manglik cancels the dosha. Many modern astrologers also consider the strength of Mars and benefic aspects before declaring a full Manglik Dosha.</p>
            </div>
            <div className="faq-seo-item">
              <h4>What information is needed for horoscope matching?</h4>
              <p>You need the exact date of birth, time of birth, and place of birth for both the bride and groom. The birth star (Nakshatra) and Moon sign (Rashi) are automatically calculated from this data. For maximum accuracy, ensure the birth time is correct — even a 30-minute difference can sometimes change the Nakshatra pada, affecting certain Porutham calculations.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchMaking;
